var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classNames as cn, getColumnWidthWithGap, ThemeUtils } from '../../../../../utils';
import { ButtonVariants } from '../../../../atoms/button';
import { IconButtonStyles } from '../../../../atoms/icon-button';
import { Link, LinkFontSizes, LinkUnderlineOptions } from '../../../../atoms/link';
import { Text, TextHtmlTags, TextHyphens, TextVariants } from '../../../../atoms/text';
import { Container, ContainerDisplays, ContainerFlexAlignItems, ContainerFlexDirections, ContainerFlexGaps, ContainerFlexJustifyContents, ContainerFlexWraps, ContainerHeights, ContainerPaddings, ContainerPositions, ContainerPositionSizes, ContainerWidths } from '../../../../templates/container';
import { Dialog } from '../../../../templates/dialog';
import { ComponentOverflows } from '../../../../types';
import { Filter } from '../filter';
import { ProductCard } from '../product-card';
import { Sorting } from '../sorting';
import { COLOR_DOT_SIZE, colorDotsInterpolation, MAX_COLORS_PER_VIEW } from './utils';
var SR_ONLY_CLASS_NAME = 'sr-only';
var FILTER_DIALOG_ID = 'filter-dialog';
var ProductCardContainer = styled(Container, {
    name: 'ProductCardContainer',
    overridesResolver: function (wrapperProps, styles) { return [styles.root]; }
})({});
var HeaderComponent = memo(function (_a) {
    var header = _a.header;
    return (React.createElement(Text.TextSection, null,
        (header === null || header === void 0 ? void 0 : header.title) && (React.createElement(Text, { component: TextHtmlTags.H1, variant: TextVariants.H2, content: header === null || header === void 0 ? void 0 : header.title, hyphens: TextHyphens.None })),
        (header === null || header === void 0 ? void 0 : header.description) && (React.createElement(Text, { isHtml: true, component: TextHtmlTags.P, variant: TextVariants.Body1, content: header === null || header === void 0 ? void 0 : header.description }))));
});
var FiltersComponent = memo(function (_a) {
    var _b, _c;
    var _d = _a.withTitle, withTitle = _d === void 0 ? false : _d, filtersData = _a.filtersData, sortData = _a.sortData, dialogButtons = _a.dialogButtons;
    return (React.createElement(React.Fragment, null,
        withTitle && (React.createElement(Container, { display: ContainerDisplays.Flex, flexJustifyContent: ContainerFlexJustifyContents.SpaceBetween, flexAlignItems: ContainerFlexAlignItems.Center, flexGap: ContainerFlexGaps.Size_2 },
            React.createElement(Text, { component: TextHtmlTags.H3, variant: TextVariants.H5, content: filtersData === null || filtersData === void 0 ? void 0 : filtersData.title, hyphens: TextHyphens.None }),
            React.createElement(Link, __assign({ fontSize: LinkFontSizes.Body2, underline: LinkUnderlineOptions.Always }, dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.resetProps)))), (_b = filtersData === null || filtersData === void 0 ? void 0 : filtersData.filterList) === null || _b === void 0 ? void 0 :
        _b.map(function (filter) { return (React.createElement(Filter, __assign({ key: filter.title }, filter))); }),
        (sortData === null || sortData === void 0 ? void 0 : sortData.title) && Boolean((_c = sortData === null || sortData === void 0 ? void 0 : sortData.radioButtons) === null || _c === void 0 ? void 0 : _c.length) && (React.createElement(Sorting, __assign({}, sortData)))));
});
var DialogComponent = memo(function (_a) {
    var _b;
    var dialogButtons = _a.dialogButtons, setOpen = _a.setOpen, restProps = __rest(_a, ["dialogButtons", "setOpen"]);
    var actions = [
        __assign(__assign({}, dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.applyProps), { onClick: function (e) {
                var _a, _b;
                (_b = (_a = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.applyProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                setOpen(false);
            } }),
        __assign(__assign({}, dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.resetProps), { buttonVariant: ButtonVariants.Outlined, onClick: function (e) {
                var _a, _b;
                (_b = (_a = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.resetProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            } })
    ];
    var handleClose = useCallback(function () {
        var _a, _b;
        if (restProps.isOpen) {
            (_b = (_a = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.closeProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
            setOpen(false);
        }
    }, [dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.closeProps, restProps.isOpen, setOpen]);
    var handleCloseRef = useRef(handleClose);
    useEffect(function () {
        handleCloseRef.current = handleClose;
    }, [handleClose]);
    useEffect(function () { return function () {
        handleCloseRef === null || handleCloseRef === void 0 ? void 0 : handleCloseRef.current();
    }; }, []);
    return (React.createElement(Dialog, __assign({}, restProps, { actions: actions, fullScreen: true, dismissible: true, closeButtonStyle: IconButtonStyles.Basic, closeAriaLabel: (_b = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.closeProps) === null || _b === void 0 ? void 0 : _b['aria-label'] })));
});
var PGL = function (_a) {
    var _b, _c, _d, _e, _f;
    var header = _a.header, filtersData = _a.filtersData, sortData = _a.sortData, productList = _a.productList, dialogButtons = _a.dialogButtons;
    var theme = useTheme();
    var itemsContainer = useRef(null);
    var _g = useState(MAX_COLORS_PER_VIEW), maxColorsPerView = _g[0], setMaxColorsPerView = _g[1];
    var _h = useState(false), open = _h[0], setOpen = _h[1];
    var isMoblet = Number(ThemeUtils.getResponsiveCurrentValue([1, 2, 3, 4])) < 3;
    var showHeader = Boolean((header === null || header === void 0 ? void 0 : header.title) || (header === null || header === void 0 ? void 0 : header.description));
    var itemWidth = {
        xs: ContainerWidths.Full,
        sm: getColumnWidthWithGap(1, 2, ContainerPaddings.Size_5),
        md: getColumnWidthWithGap(1, 2, ContainerPaddings.Size_4),
        lg: getColumnWidthWithGap(1, 3, ContainerPaddings.Size_4)
    };
    var colorDotWidth = Number(ThemeUtils.getComputedSpacing(COLOR_DOT_SIZE, true).replace('px', ''));
    var showFilters = useMemo(function () {
        var _a, _b;
        return Boolean((_a = filtersData === null || filtersData === void 0 ? void 0 : filtersData.filterList) === null || _a === void 0 ? void 0 : _a.length) ||
            Boolean((_b = sortData === null || sortData === void 0 ? void 0 : sortData.radioButtons) === null || _b === void 0 ? void 0 : _b.length);
    }, [(_b = filtersData === null || filtersData === void 0 ? void 0 : filtersData.filterList) === null || _b === void 0 ? void 0 : _b.length, (_c = sortData === null || sortData === void 0 ? void 0 : sortData.radioButtons) === null || _c === void 0 ? void 0 : _c.length]);
    var gap = useMemo(function () {
        var _a, _b, _c, _d, _e, _f;
        return (Number((_f = (_e = (_d = (_c = (_b = (_a = theme.components) === null || _a === void 0 ? void 0 : _a.MuiFormGroup) === null || _b === void 0 ? void 0 : _b.styleOverrides) === null || _c === void 0 ? void 0 : _c.root) === null || _d === void 0 ? void 0 : _d.gap) === null || _e === void 0 ? void 0 : _e.replace('rem', '')) !== null && _f !== void 0 ? _f : '') *
            16) /
            1e2;
    }, [(_f = (_e = (_d = theme.components) === null || _d === void 0 ? void 0 : _d.MuiFormGroup) === null || _e === void 0 ? void 0 : _e.styleOverrides) === null || _f === void 0 ? void 0 : _f.root]);
    var openDialog = useCallback(function (e) {
        var _a, _b;
        (_b = (_a = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.openProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        setOpen(true);
    }, [dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.openProps]);
    var closeDialog = useCallback(function (e) {
        var _a, _b;
        (_b = (_a = dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.closeProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        setOpen(false);
    }, [dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.closeProps]);
    var calcMaxColorsPerView = useCallback(function (node) {
        var first = node === null || node === void 0 ? void 0 : node.querySelector(":scope > :not(.".concat(SR_ONLY_CLASS_NAME, ")"));
        var value = colorDotsInterpolation(colorDotWidth, first === null || first === void 0 ? void 0 : first.offsetWidth, gap);
        setMaxColorsPerView(value);
    }, [colorDotWidth, gap]);
    var paddingHeader = ThemeUtils.getResponsiveProp([
        showHeader ? { b: ContainerPaddings.Size_5 } : {},
        null,
        null,
        showHeader ? { b: ContainerPaddings.Size_6 } : {}
    ]);
    var paddingFilterLink = ThemeUtils.getResponsiveProp([
        showHeader
            ? { b: ContainerPaddings.Size_4 }
            : { b: ContainerPaddings.Size_5 }
    ]);
    useEffect(function () {
        if (!isMoblet && open) {
            setOpen(false);
        }
    }, [isMoblet, open]);
    // eslint-disable-next-line consistent-return
    useEffect(function () {
        var node = itemsContainer === null || itemsContainer === void 0 ? void 0 : itemsContainer.current;
        if (node && (productList === null || productList === void 0 ? void 0 : productList.length)) {
            var observer_1 = new ResizeObserver(function () {
                calcMaxColorsPerView(node);
            });
            calcMaxColorsPerView(node);
            observer_1.observe(node);
            return function () {
                observer_1.unobserve(node);
                observer_1.disconnect();
            };
        }
    }, [calcMaxColorsPerView, productList === null || productList === void 0 ? void 0 : productList.length]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { padding: {
                xs: { x: ContainerPaddings.Size_4 },
                md: { x: ContainerPaddings.Size_5 },
                lg: { x: ContainerPaddings.Size_6 },
                xl: { x: ContainerPaddings.Size_7 }
            } },
            React.createElement(Container, { display: ContainerDisplays.Flex, flexJustifyContent: ContainerFlexJustifyContents.Center, flexWrap: ContainerFlexWraps.NoWrap, flexGap: {
                    md: ContainerFlexGaps.Size_5,
                    lg: ContainerFlexGaps.Size_6
                }, width: { md: ContainerWidths.Boxed } },
                React.createElement(Container, { position: ContainerPositions.Sticky, positionCoordinates: {
                        md: { top: ContainerPositionSizes.Size_0 }
                    }, flexShrink: 0, display: {
                        xs: ContainerDisplays.None,
                        md: showFilters ? ContainerDisplays.Flex : ContainerDisplays.None
                    }, flexDirection: ContainerFlexDirections.Column, flexWrap: ContainerFlexWraps.NoWrap, flexGap: {
                        md: ContainerFlexGaps.Size_5
                    }, maxHeight: ContainerHeights.FullScreen, width: {
                        md: '300px',
                        xl: '352px'
                    }, padding: {
                        md: {
                            y: ContainerPaddings.Size_2,
                            r: ContainerPaddings.Size_2
                        }
                    }, overflow: {
                        md: { x: ComponentOverflows.Hidden, y: ComponentOverflows.Auto }
                    } },
                    React.createElement(FiltersComponent, { withTitle: true, filtersData: filtersData, sortData: sortData, dialogButtons: dialogButtons })),
                React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexWrap: ContainerFlexWraps.NoWrap, width: ContainerWidths.Full },
                    React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexWrap: ContainerFlexWraps.NoWrap, padding: paddingHeader },
                        React.createElement(Container, { display: {
                                xs: showFilters
                                    ? ContainerDisplays.Block
                                    : ContainerDisplays.None,
                                md: ContainerDisplays.None
                            }, padding: paddingFilterLink },
                            React.createElement(Link, __assign({ icon: "pmi-icon-filter", linkUrl: "#".concat(FILTER_DIALOG_ID), underline: LinkUnderlineOptions.Always }, dialogButtons === null || dialogButtons === void 0 ? void 0 : dialogButtons.openProps, { onClick: openDialog }))),
                        showHeader && React.createElement(HeaderComponent, { header: header })),
                    React.createElement(Container, { innerRef: itemsContainer, display: ContainerDisplays.Flex, flexDirection: {
                            xs: ContainerFlexDirections.Column,
                            sm: ContainerFlexDirections.Row
                        }, flexAlignItems: ContainerFlexAlignItems.Strecth, flexWrap: {
                            sm: ContainerFlexWraps.Wrap
                        }, flexGap: {
                            xs: ContainerFlexGaps.Size_6,
                            sm: ContainerFlexGaps.Size_5,
                            md: {
                                x: ContainerFlexGaps.Size_4,
                                y: ContainerFlexGaps.Size_6
                            }
                        } }, productList === null || productList === void 0 ? void 0 : productList.map(function (_a, key) {
                        var srOnly = _a.srOnly, colorSelector = _a.colorSelector, rest = __rest(_a, ["srOnly", "colorSelector"]);
                        return (React.createElement(ProductCardContainer, { key: "product-card-".concat(key), className: cn(srOnly && SR_ONLY_CLASS_NAME), width: itemWidth },
                            React.createElement(ProductCard, __assign({ colorSelector: __assign(__assign({}, colorSelector), { maxColorsPerView: (colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.maxColorsPerView)
                                        ? Math.min(maxColorsPerView, colorSelector === null || colorSelector === void 0 ? void 0 : colorSelector.maxColorsPerView)
                                        : maxColorsPerView }) }, rest))));
                    }))))),
        React.createElement(DialogComponent, { id: FILTER_DIALOG_ID, title: filtersData === null || filtersData === void 0 ? void 0 : filtersData.title, titleTag: TextHtmlTags.H3, isOpen: open && isMoblet, setOpen: function (value) { return setOpen(value); }, dialogButtons: dialogButtons, onClose: closeDialog },
            React.createElement(Container, { display: ContainerDisplays.Flex, flexDirection: ContainerFlexDirections.Column, flexWrap: ContainerFlexWraps.NoWrap, flexGap: ContainerFlexGaps.Size_5 },
                React.createElement(FiltersComponent, { filtersData: filtersData, sortData: sortData })))));
};
export { PGL as ProductGridList };
